import { Component, signal, computed, OnInit, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GamesService } from '../../../services/games/games.service';
import { GameDetailsModal } from '../../../components/modals/game-details-modal/game-details-modal';

export interface Game {
  id: string;
  title: string;
  coverUrl: string;
  platforms: string[];
  developer: string;
  genre: string;
  releaseYear: number;
  lastPlayed: string;
  sizeGb: number;
  installed: boolean;
  executable?: string;
  customInstallPath?: string;
  customExecutable?: string;
  appid?: number;
}

@Component({
  selector: 'app-library',
  standalone: true,
  imports: [CommonModule, FormsModule, GameDetailsModal],
  templateUrl: './library.html',
  styleUrl: './library.scss'
})
export class Library implements OnInit {
  private gamesService = inject(GamesService);
  private location = inject(Location);

  public searchQuery = signal('');
  public selectedPlatform = signal('All games');
  public selectedGenre = signal('All');
  public sortBy = signal('title-asc');
  public selectedGame = signal<Game | null>(null);
  public sidebarOpen = signal(false);
  public isEditingExecutable = signal(false);

  public navHistory = signal<string[]>(['All games']);
  public navIndex = signal<number>(0);

  public canGoBack = computed(() => this.navIndex() > 0);
  public canGoForward = computed(() => this.navIndex() < this.navHistory().length - 1);

  public games = signal<Game[]>([
    {
      id: 'quake-2',
      title: 'Quake II',
      coverUrl: 'images/library/covers/quake-2.jpg',
      platforms: ['RivalForge', 'Steam'],
      developer: 'id Software',
      genre: 'Shooter',
      releaseYear: 1997,
      lastPlayed: 'Never',
      sizeGb: 1.0,
      installed: false,
      executable: 'quake2.exe'
    },
    {
      id: 'quake-1',
      title: 'Quake',
      coverUrl: 'images/library/covers/quake-1.jpg',
      platforms: ['RivalForge', 'Steam'],
      developer: 'id Software',
      genre: 'Shooter',
      releaseYear: 1996,
      lastPlayed: 'Never',
      sizeGb: 0.5,
      installed: false,
      executable: 'quake.exe'
    },
    {
      id: 'quake-3-arena',
      title: 'Quake III Arena',
      coverUrl: 'images/library/covers/quake-3-arena.jpg',
      platforms: ['RivalForge', 'Steam'],
      developer: 'id Software',
      genre: 'Shooter',
      releaseYear: 1999,
      lastPlayed: 'Never',
      sizeGb: 1.5,
      installed: false,
      executable: 'quake3.exe'
    },
    {
      id: 'team-fortress-2',
      title: 'Team Fortress 2',
      coverUrl: 'images/library/covers/team-fortress-2.jpg',
      platforms: ['Steam', 'RivalForge'],
      developer: 'Valve',
      genre: 'Shooter',
      releaseYear: 2007,
      lastPlayed: 'Never',
      sizeGb: 15.0,
      installed: false,
      executable: 'tf_win64.exe'
    },
    {
      id: 'medal-of-honor-allied-assault',
      title: 'Medal of Honor: Allied Assault',
      coverUrl: 'images/library/covers/medal-of-honor-allied-assault.jpg',
      platforms: ['RivalForge', 'Origin'],
      developer: '2015, Inc. / EA',
      genre: 'Shooter',
      releaseYear: 2002,
      lastPlayed: 'Never',
      sizeGb: 3.0,
      installed: false,
      executable: 'MOHAA.exe'
    },
    {
      id: 'cod-bo3',
      title: 'Call of Duty: Black Ops III',
      coverUrl: 'images/library/covers/cod-bo3.jpg',
      platforms: ['Steam', 'Activision'],
      developer: 'Treyarch',
      genre: 'Shooter',
      releaseYear: 2015,
      lastPlayed: 'Never',
      sizeGb: 100,
      installed: false,
      executable: 'blackops3.exe',
      appid: 311210
    },
    {
      id: 'cs2',
      title: 'Counter-Strike 2',
      coverUrl: 'images/library/covers/cs2.jpg',
      platforms: ['Steam'],
      developer: 'Valve',
      genre: 'Shooter',
      releaseYear: 2023,
      lastPlayed: 'Never',
      sizeGb: 40,
      installed: false,
      executable: 'cs2.exe',
      appid: 730
    },
    {
      id: 'doom2016',
      title: 'DOOM',
      coverUrl: 'images/library/covers/doom2016.jpg',
      platforms: ['Steam', 'Bethesda'],
      developer: 'id Software',
      genre: 'Shooter',
      releaseYear: 2016,
      lastPlayed: 'Never',
      sizeGb: 55,
      installed: false,
      executable: 'DOOMx64.exe',
      appid: 379720
    }
  ]);

  public platformsList = [
    { name: 'Recent', icon: 'bi-clock', count: null },
    { name: 'All games', icon: 'bi-grid-fill', count: null },
    { name: 'Installed', icon: 'bi-check-circle-fill', count: null },
    { name: 'RivalForge', icon: 'bi-lightning-charge-fill', count: null },
    { name: 'Steam', icon: 'bi-bookmark-fill', count: null },
    { name: 'GOG.com', icon: 'bi-bookmark-fill', count: null },
    { name: 'Epic', icon: 'bi-bookmark-fill', count: null }
  ];

  public async ngOnInit() {
    await this.refreshGameStatuses();
  }

  public async refreshGameStatuses() {
    const registeredGames = await this.gamesService.getAllGames();
    if (!registeredGames || registeredGames.length === 0) return;

    this.games.update(currentList => {
      const updatedList = [...currentList];

      for (const reg of registeredGames) {
        const existingIndex = updatedList.findIndex(g => g.id === reg.id);
        if (existingIndex !== -1) {
          updatedList[existingIndex] = {
            ...updatedList[existingIndex],
            installed: !!reg.installed,
            customInstallPath: reg.customInstallPath,
            customExecutable: reg.customExecutable,
            executable: reg.customExecutable || reg.executable || updatedList[existingIndex].executable,
            coverUrl: reg.coverUrl || updatedList[existingIndex].coverUrl
          };
        } else {
          updatedList.unshift({
            id: reg.id,
            title: reg.name,
            coverUrl: reg.coverUrl || 'images/library/covers/doom2016.jpg',
            platforms: ['RivalForge'],
            developer: reg.developer || 'Unknown',
            genre: reg.genre || 'Action',
            releaseYear: reg.releaseYear || 2000,
            lastPlayed: 'Never',
            sizeGb: reg.sizeGb || 1.0,
            installed: !!reg.installed,
            customInstallPath: reg.customInstallPath,
            customExecutable: reg.customExecutable,
            executable: reg.customExecutable || reg.executable
          });
        }
      }
      return updatedList;
    });
  }

  // Dynamic filter computed property
  public filteredGames = computed(() => {
    let result = [...this.games()];

    // Platform Filter
    const activePlatform = this.selectedPlatform();
    if (activePlatform === 'Recent') {
      result = result.filter(g => g.lastPlayed !== 'Never' && !g.lastPlayed.includes('year') && !g.lastPlayed.includes('10 years'));
    } else if (activePlatform === 'Installed') {
      result = result.filter(g => g.installed);
    } else if (activePlatform !== 'All games') {
      result = result.filter(g => g.platforms.some(p => p.toLowerCase() === activePlatform.toLowerCase()));
    }

    // Genre Filter
    const activeGenre = this.selectedGenre();
    if (activeGenre !== 'All') {
      result = result.filter(g => g.genre === activeGenre);
    }

    // Search Query
    const query = this.searchQuery().toLowerCase().trim();
    if (query) {
      result = result.filter(g =>
        g.title.toLowerCase().includes(query) ||
        g.developer.toLowerCase().includes(query) ||
        g.genre.toLowerCase().includes(query)
      );
    }

    // Sorting
    const sort = this.sortBy();
    if (sort === 'title-asc') {
      result.sort((a, b) => a.title.localeCompare(b.title));
    } else if (sort === 'title-desc') {
      result.sort((a, b) => b.title.localeCompare(a.title));
    } else if (sort === 'year-desc') {
      result.sort((a, b) => b.releaseYear - a.releaseYear);
    } else if (sort === 'size-desc') {
      result.sort((a, b) => b.sizeGb - a.sizeGb);
    }

    return result;
  });

  public selectPlatform(platform: string, pushHistory = true) {
    if (pushHistory && platform !== this.selectedPlatform()) {
      const currentHistory = this.navHistory().slice(0, this.navIndex() + 1);
      currentHistory.push(platform);
      this.navHistory.set(currentHistory);
      this.navIndex.set(currentHistory.length - 1);
    }
    this.selectedPlatform.set(platform);
    this.sidebarOpen.set(false);
  }

  public goBack() {
    if (this.canGoBack()) {
      const newIndex = this.navIndex() - 1;
      this.navIndex.set(newIndex);
      this.selectPlatform(this.navHistory()[newIndex], false);
    } else {
      this.location.back();
    }
  }

  public goForward() {
    if (this.canGoForward()) {
      const newIndex = this.navIndex() + 1;
      this.navIndex.set(newIndex);
      this.selectPlatform(this.navHistory()[newIndex], false);
    } else {
      this.location.forward();
    }
  }

  public openGameDetails(game: Game) {
    this.selectedGame.set(game);
    this.isEditingExecutable.set(false);
  }

  public closeGameDetails() {
    this.selectedGame.set(null);
    this.isEditingExecutable.set(false);
  }

  public async browseGamePath(game: Game, event?: Event) {
    if (event) event.stopPropagation();

    const res = await this.gamesService.browseGamePath(game.id);
    if (res.success && (res.path || res.executable)) {
      const isInstalled = res.installed !== undefined ? res.installed : true;
      this.games.update(current =>
        current.map(g => {
          if (g.id === game.id) {
            return {
              ...g,
              customInstallPath: res.path || g.customInstallPath,
              customExecutable: res.executable || g.customExecutable,
              installed: isInstalled
            };
          }
          return g;
        })
      );
      const selected = this.selectedGame();
      if (selected && selected.id === game.id) {
        this.selectedGame.set({
          ...selected,
          customInstallPath: res.path || selected.customInstallPath,
          customExecutable: res.executable || selected.customExecutable,
          installed: isInstalled
        });
      }
    }
  }

  public async updateGamePath(game: Game, newPath: string) {
    const trimmed = newPath.trim();
    if (trimmed === (game.customInstallPath || '')) return;

    const res = await this.gamesService.setGamePath(game.id, trimmed);
    const isInstalled = res.installed !== undefined ? res.installed : game.installed;

    this.games.update(current =>
      current.map(g => g.id === game.id ? { ...g, customInstallPath: trimmed, installed: isInstalled } : g)
    );

    const selected = this.selectedGame();
    if (selected && selected.id === game.id) {
      this.selectedGame.set({ ...selected, customInstallPath: trimmed, installed: isInstalled });
    }
  }

  public async updateGameExecutable(game: Game, newExecutable: string) {
    const trimmed = newExecutable.trim();
    if (trimmed === (game.customExecutable || '')) return;

    const res = await this.gamesService.setGameExecutable(game.id, trimmed);
    const isInstalled = res.installed !== undefined ? res.installed : game.installed;

    this.games.update(current =>
      current.map(g => g.id === game.id ? { ...g, customExecutable: trimmed, installed: isInstalled } : g)
    );

    const selected = this.selectedGame();
    if (selected && selected.id === game.id) {
      this.selectedGame.set({ ...selected, customExecutable: trimmed, installed: isInstalled });
    }
  }

  public async playLaunchGame(game: Game, event?: Event) {
    if (event) event.stopPropagation();

    if (!game.installed) {
      await this.browseGamePath(game);
      return;
    }

    const launched = await this.gamesService.launchGame(game.id);
    if (launched) {
      this.games.update(current =>
        current.map(g => g.id === game.id ? { ...g, lastPlayed: 'Just now' } : g)
      );
      const selected = this.selectedGame();
      if (selected && selected.id === game.id) {
        this.selectedGame.set({ ...selected, lastPlayed: 'Just now' });
      }
    } else {
      console.warn('Game launch was not successful or container API not present.');
    }
  }

  public onGameUpdated(event: { id: string; customInstallPath?: string; customExecutable?: string; installed: boolean }) {
    this.games.update(current =>
      current.map(g => {
        if (g.id === event.id) {
          return {
            ...g,
            customInstallPath: event.customInstallPath !== undefined ? event.customInstallPath : g.customInstallPath,
            customExecutable: event.customExecutable !== undefined ? event.customExecutable : g.customExecutable,
            installed: event.installed
          };
        }
        return g;
      })
    );

    const selected = this.selectedGame();
    if (selected && selected.id === event.id) {
      this.selectedGame.set({
        ...selected,
        customInstallPath: event.customInstallPath !== undefined ? event.customInstallPath : selected.customInstallPath,
        customExecutable: event.customExecutable !== undefined ? event.customExecutable : selected.customExecutable,
        installed: event.installed
      });
    }
  }

  public onGameLaunched(game: Game) {
    this.games.update(current =>
      current.map(g => g.id === game.id ? { ...g, lastPlayed: 'Just now' } : g)
    );
    const selected = this.selectedGame();
    if (selected && selected.id === game.id) {
      this.selectedGame.set({ ...selected, lastPlayed: 'Just now' });
    }
  }
}
